import { Controller } from "@hotwired/stimulus"

const EMPTY_VALUE = "—"

type StepShownDetail = {
  step?: HTMLElement
}

export default class extends Controller<HTMLFormElement> {
  static targets = ["confirmationStep", "contacts", "contactsHeading"]

  declare readonly confirmationStepTarget: HTMLElement
  declare readonly hasContactsTarget: boolean
  declare readonly contactsTarget: HTMLElement
  declare readonly contactsHeadingTarget: HTMLElement

  connect() {
    window.addEventListener("form-steps:step-shown", this.handleStepShown)
  }

  disconnect() {
    window.removeEventListener("form-steps:step-shown", this.handleStepShown)
  }

  private handleStepShown = (event: Event) => {
    const shownStep = (event as CustomEvent<StepShownDetail>).detail?.step
    if (shownStep !== this.confirmationStepTarget) return

    this.updateFields()
    this.updateTransports()
    this.updateScale()
    this.updateCoordinates()
    this.updateContacts()
  }

  private updateFields() {
    this.confirmationStepTarget.querySelectorAll<HTMLElement>("[data-confirm-field-for]").forEach((summary) => {
      const fieldId = summary.dataset.confirmFieldFor
      const field = fieldId ? document.getElementById(fieldId) : null
      summary.textContent = this.displayValue(field, summary.dataset.confirmUnit)
    })
  }

  private displayValue(field: HTMLElement | null, unit?: string) {
    if (field instanceof HTMLSelectElement) {
      return field.value ? field.selectedOptions[0]?.textContent?.trim() || EMPTY_VALUE : EMPTY_VALUE
    }

    if (field instanceof HTMLInputElement && field.type === "checkbox") {
      return field.checked ? "あり" : "なし"
    }

    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
      const value = field.value.trim()
      if (!value) return EMPTY_VALUE
      // type="date" 等も valueAsNumber を持ち日時をミリ秒で返すため、桁区切りは数値入力に限る
      const numeric = field instanceof HTMLInputElement && field.type === "number" ? field.valueAsNumber : NaN
      const display = Number.isFinite(numeric) ? numeric.toLocaleString("ja-JP") : value
      return unit ? `${display} ${unit}` : display
    }

    return EMPTY_VALUE
  }

  private updateTransports() {
    this.confirmationStepTarget.querySelectorAll<HTMLElement>("[data-confirm-transport]").forEach((summary) => {
      const index = summary.dataset.confirmTransport
      if (!index) return

      const line = this.enabledFieldValue(`property[transport_line_${index}]`)
      const station = this.enabledFieldValue(`property[transport_station_${index}]`)
      const walk = this.enabledFieldValue(`property[transport_walk_minutes_${index}]`)
      const parts = [line, station ? `「${station}」` : "", walk ? `徒歩${walk}分` : ""].filter(Boolean)
      summary.textContent = parts.join(" ") || EMPTY_VALUE
    })
  }

  private enabledFieldValue(name: string) {
    const fields = this.element.querySelectorAll<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`)
    const field = Array.from(fields).find((candidate) => !candidate.disabled)

    if (!field || !field.value) return ""
    if (field instanceof HTMLSelectElement) return field.selectedOptions[0]?.textContent?.trim() || ""
    return field.value.trim()
  }

  private updateScale() {
    const summary = this.confirmationStepTarget.querySelector<HTMLElement>("[data-confirm-scale]")
    if (!summary) return

    const above = this.inputValue("property_floors_above_ground")
    const below = this.inputValue("property_floors_below_ground")
    summary.textContent = above || below ? `地上${above || "0"}階 / 地下${below || "0"}階` : EMPTY_VALUE
  }

  private updateCoordinates() {
    const summary = this.confirmationStepTarget.querySelector<HTMLElement>("[data-confirm-coordinates]")
    if (!summary) return

    const latitude = this.inputValue("property_property_location_latitude")
    const longitude = this.inputValue("property_property_location_longitude")
    summary.textContent = [latitude, longitude].filter(Boolean).join(" / ") || EMPTY_VALUE
  }

  private inputValue(id: string) {
    const field = document.getElementById(id)
    return field instanceof HTMLInputElement ? field.value.trim() : ""
  }

  private updateContacts() {
    // 窓口情報を持たないフォーム（クイック登録）では確認欄そのものが無い
    if (!this.hasContactsTarget) return

    const contacts = Array.from(this.element.querySelectorAll<HTMLElement>("[data-form-confirm-contact]"))
      .filter((contact) => !this.checked(contact, "_destroy"))
      .map((contact) => ({
        type: this.contactFieldValue(contact, "contact_type"),
        companyName: this.contactFieldValue(contact, "company_name"),
        companyNameKana: this.contactFieldValue(contact, "company_name_kana"),
        tel: this.contactFieldValue(contact, "tel"),
        address: this.contactFieldValue(contact, "address"),
        personName: this.contactFieldValue(contact, "person_name"),
        personDepartment: this.contactFieldValue(contact, "person_department"),
        personTel: this.contactFieldValue(contact, "person_tel")
      }))
      .filter((contact) => Object.values(contact).some(Boolean))
      .slice(0, 3)

    this.contactsHeadingTarget.textContent = `窓口情報（${contacts.length}件）`
    this.contactsTarget.replaceChildren()

    contacts.forEach((contact, index) => {
      this.appendContactRow(`窓口${index + 1}：分類`, contact.type)
      this.appendContactRow("会社名", contact.companyName)
      this.appendContactRow("会社名カナ", contact.companyNameKana)
      this.appendContactRow("会社電話番号", contact.tel)
      this.appendContactRow("会社住所", contact.address)
      this.appendContactRow("担当者", [contact.personName, contact.personDepartment].filter(Boolean).join("／"))
      this.appendContactRow("担当者電話番号", contact.personTel)
    })

    if (contacts.length === 0) this.appendContactRow("窓口", "未登録")
  }

  private contactFieldValue(contact: HTMLElement, attribute: string) {
    const field = contact.querySelector<HTMLInputElement | HTMLSelectElement>(`[name$="[${attribute}]"]`)
    if (!field || !field.value) return ""
    if (field instanceof HTMLSelectElement) return field.selectedOptions[0]?.textContent?.trim() || ""
    return field.value.trim()
  }

  private checked(contact: HTMLElement, attribute: string) {
    const field = contact.querySelector<HTMLInputElement>(`input[type="checkbox"][name$="[${attribute}]"]`)
    return field?.checked || false
  }

  private appendContactRow(label: string, value: string) {
    const row = document.createElement("div")
    const term = document.createElement("dt")
    const description = document.createElement("dd")

    row.className = "c-confirm-row text-sm"
    term.className = "w-40 min-w-40 text-bodydark2"
    description.className = "font-medium text-black"
    term.textContent = label
    description.textContent = value || EMPTY_VALUE
    row.append(term, description)
    this.contactsTarget.append(row)
  }
}
